import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Chip, Menu, type ElementoMenu } from '@tallerya/ui';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { useTipo } from '../catalogos/datos';
import { leerDetalle } from './CamposDelTipo';
import { useCambiosActivos, useFichaActivo, type FichaActivo as Ficha } from './datos';
import { EliminarActivo } from './EliminarActivo';
import { PanelHistorial } from './PanelHistorial';

// La ficha del activo (#187): consultarlo sin abrirlo como si se fuera a cambiar.
// La ganó con el historial, por la regla de `estructura-del-proyecto.md` §7. No
// hay frame en el Figma: sigue a `FichaCliente.tsx` —migas, cabecera con sus
// acciones y secciones de solo lectura—, y el Figma se ajusta después.
//
// Lo propio de esta pantalla:
//
//   - **se nombra por la placa**, que es como el taller reconoce un carro; sin
//     ella, por el código. La marca, el modelo y el año van debajo;
//   - los detalles son **los campos encendidos de su tipo**, como en el
//     formulario, leídos con `leerDetalle`;
//   - las órdenes y las fotos del activo no están todavía: quedaron fuera del
//     issue, y las fotos se siguen viendo en la edición.
//
// Se llega desde el listado de activos y desde la ficha del cliente. Las dos
// mandan algo en el estado, y en los dos casos «Atrás» vuelve a donde estaba.

const RUTA = '/activos';

export function FichaActivo() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const consulta = useFichaActivo(id);
  useTituloPantalla(consulta.data ? titulo(consulta.data) : t('admin.activos.titulo'));

  // Al eliminar desde aquí no se llega a ver «no existe»: `eliminar` no vuelve a
  // pedir la ficha eliminada, y se sigue viendo hasta que se sale.
  const ficha = consulta.data;

  if (consulta.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (!ficha) {
    const noExiste = consulta.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.activos.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.activos.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar(RUTA)}>
          {t('admin.activos.volver')}
        </Boton>
      </section>
    );
  }

  return <Contenido ficha={ficha} />;
}

/** La placa o la serie; sin ella, el código; sin nada, el tipo. */
function titulo(ficha: Ficha): string {
  return ficha.identifier ?? ficha.code ?? ficha.assetTypeName;
}

function Contenido({ ficha }: { ficha: Ficha }) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosActivos();
  const { hacer, problema } = useAccionDeFila();
  const [eliminando, setEliminando] = useState(false);
  // El historial empieza cerrado y no se pide hasta abrirlo (#193).
  const [viendoHistorial, setViendoHistorial] = useState(false);
  const id = String(ficha.id);
  const tipo = useTipo(String(ficha.assetTypeId));
  // Mientras carga, sin definir; si no se pudo leer, nulo: el historial sale igual.
  const elTipo = tipo.isPending ? undefined : (tipo.data ?? null);

  // Desde el listado o desde la ficha del cliente: hay una pantalla detrás, y se
  // vuelve a ella. Quien entró por un enlace va al listado limpio.
  const origen = useLocation().state as { desdeListado?: boolean; desdeFicha?: boolean } | null;
  const hayAtras = Boolean(origen?.desdeListado || origen?.desdeFicha);
  const alEliminar = () => void (hayAtras ? navegar(-1) : navegar(RUTA, { replace: true }));

  const nombre = { nombre: [ficha.code, ficha.identifier].filter(Boolean).join(' · ') };
  const masAcciones: ElementoMenu[] = [
    ficha.isActive
      ? {
          etiqueta: t('admin.activos.menu.desactivar'),
          onSeleccionar: () => void hacer(cambios.cambiarEstado(id, false), t('admin.activos.aviso.desactivado', nombre)),
        }
      : {
          etiqueta: t('admin.activos.menu.activar'),
          onSeleccionar: () => void hacer(cambios.cambiarEstado(id, true), t('admin.activos.aviso.activado', nombre)),
        },
    { tipo: 'divisor' },
    { etiqueta: t('admin.activos.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminando(true) },
  ];

  // «Toyota Hilux · 2018»: lo que describe al carro, debajo de su placa.
  const resumen = [[ficha.brandName, ficha.modelName].filter(Boolean).join(' '), ficha.year]
    .filter(Boolean)
    .join(' · ');

  // Los campos encendidos, como en el formulario. Lo guardado en uno apagado no se
  // muestra: tampoco se puede editar.
  const campos = (tipo.data?.fields ?? []).filter((c) => c.isActive);

  return (
    <>
      <nav aria-label={t('admin.clientes.ficha.migas')}>
        <ol className="ficha__migas">
          <li>
            <Link to={RUTA}>{t('admin.activos.titulo')}</Link>
          </li>
          <li aria-current="page">{titulo(ficha)}</li>
        </ol>
      </nav>

      {problema && <AvisoError aviso={{ titulo: t('admin.activos.error.titulo'), texto: problema }} />}

      <header className="tipos__cabecera">
        <div>
          <div className="tipos__titulo">
            <h1>{titulo(ficha)}</h1>
            <Ayuda tema={titulo(ficha)} texto={t('admin.activos.ficha.info')} tono="titulo" />
            <Chip>{ficha.assetTypeName}</Chip>
            {ficha.isActive ? (
              <Chip tono="exito">{t('admin.activos.activo')}</Chip>
            ) : (
              <Chip>{t('admin.activos.inactivo')}</Chip>
            )}
          </div>
          {resumen && <p className="tipos__resumen">{resumen}</p>}
        </div>
        <div className="tipos__acciones">
          <Boton variante="secundario" onClick={() => setViendoHistorial(true)}>
            {t('admin.historial.titulo')}
          </Boton>
          <Boton
            variante="secundario"
            // `desdeFicha`: el formulario vuelve con «Atrás», que es esta ficha. Y
            // `desdeListado`, si hay algo detrás de ella: al eliminar desde la
            // edición se salta hasta ahí.
            onClick={() => void navegar(`${RUTA}/${id}/editar`, { state: { desdeFicha: true, desdeListado: hayAtras } })}
          >
            {t('admin.activos.editar.titulo')}
          </Boton>
          <Menu
            elementos={masAcciones}
            disparador={({ alternar, atributos }) => (
              <Boton variante="secundario" soloIcono aria-label={t('admin.activos.mas')} onClick={alternar} {...atributos}>
                •••
              </Boton>
            )}
          />
        </div>
      </header>

      <div className="ficha__secciones">
        <Seccion titulo={t('admin.activos.seccion.general')}>
          <dl className="ficha__datos">
            <Dato etiqueta={t('admin.activos.codigo')}>{ficha.code ?? '—'}</Dato>
            <Dato etiqueta={ficha.identifierLabel}>{ficha.identifier ?? '—'}</Dato>
            <Dato etiqueta={t('admin.activos.marca')}>{ficha.brandName ?? '—'}</Dato>
            <Dato etiqueta={t('admin.activos.modelo')}>{ficha.modelName ?? '—'}</Dato>
            <Dato etiqueta={t('admin.activos.anio')}>{ficha.year ?? '—'}</Dato>
            <Dato etiqueta={t('admin.activos.cliente')}>
              {ficha.customerId && ficha.customerName ? (
                <Link to={`/clientes/${String(ficha.customerId)}`}>{ficha.customerName}</Link>
              ) : (
                t('admin.activos.ficha.sinCliente')
              )}
            </Dato>
          </dl>
        </Seccion>

        <Seccion
          titulo={t('admin.activos.seccion.detalles')}
          info={t('admin.activos.seccion.detallesTexto', { tipo: ficha.assetTypeName })}
        >
          {tipo.isPending ? (
            <p className="ficha__nota">{t('app.cargando')}</p>
          ) : tipo.isError ? (
            <div className="ficha__vacio" role="alert">
              <p className="ficha__vacio-titulo">{t('ui.listado.error.titulo')}</p>
              <Boton variante="secundario" onClick={() => void tipo.refetch()}>
                {t('app.reintentar')}
              </Boton>
            </div>
          ) : campos.length === 0 ? (
            <p className="ficha__nota">{t('admin.activos.sinCampos')}</p>
          ) : (
            <dl className="ficha__datos">
              {campos.map((campo) => {
                const valor = ficha.details[campo.code];
                return (
                  <Dato key={campo.code} etiqueta={campo.label}>
                    {valor === undefined || valor === null || valor === ''
                      ? '—'
                      : leerDetalle(campo, valor as string | number | boolean, t, idioma)}
                  </Dato>
                );
              })}
            </dl>
          )}
        </Seccion>

        {ficha.notes && (
          <Seccion titulo={t('admin.activos.seccion.notas')}>
            <p className="ficha__notas">{ficha.notes}</p>
          </Seccion>
        )}

        {/* Quién le cambió qué, en el panel que abre «Historial», como en la ficha del cliente (#193). */}
        <PanelHistorial activoId={id} tipo={elTipo} abierto={viendoHistorial} onCerrar={() => setViendoHistorial(false)} />
      </div>

      <EliminarActivo
        activo={eliminando ? ficha : null}
        onCerrar={() => setEliminando(false)}
        onEliminado={alEliminar}
      />
    </>
  );
}

function Seccion({ titulo, info, children }: { titulo: string; info?: string; children: ReactNode }) {
  return (
    <section className="tipos__seccion">
      <div className="tipos__seccion-cabecera">
        <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
          <h2>{titulo}</h2>
          {info && <Ayuda tema={titulo} texto={info} tono="titulo" />}
        </div>
      </div>
      {children}
    </section>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="ficha__dato">
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}
